// Contact.jsx
import React, { useState, useEffect, useRef } from "react";
import { motion, useScroll, useSpring } from "framer-motion";
import {
  FiMail,
  FiPhone,
  FiMapPin,
  FiSend,
  FiMessageSquare,
  FiUser,
  FiAtSign,
  FiArrowUpRight,
  FiArrowRight,
  FiClock,
  FiLoader,
} from "react-icons/fi";
import {
  FaInstagram,
  FaYoutube,
  FaFacebookF,
  FaWhatsapp,
  FaGithub,
} from "react-icons/fa";
import { db, collection, addDoc, auth } from "./firebase";
import emailjs from "emailjs-com";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Navbar from "./Navbar";
import Foot from "./Foot";
import Chatbot from "./Chatbot";
import "./Hero.css";     // design system: tokens, buttons, grain, .kk-progress
import "./Contact.css";  // everything unique to this page

/* ============================================================
   PARALLAX — same single rAF loop as the home page
============================================================ */
function useParallax(rootRef) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const items = Array.from(root.querySelectorAll("[data-plx]"));
    if (!items.length) return;

    let raf = 0;
    const render = () => {
      const vh = window.innerHeight;
      for (const el of items) {
        const speed = parseFloat(el.dataset.plx) || 0;
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -420 || rect.top > vh + 420) continue;
        const y = (rect.top + rect.height / 2 - vh / 2) * -speed;
        el.style.transform = `translate3d(0, ${y.toFixed(2)}px, 0)`;
      }
      raf = requestAnimationFrame(render);
    };
    raf = requestAnimationFrame(render);
    return () => cancelAnimationFrame(raf);
  }, [rootRef]);
}

/* live IST clock — same as the home hero */
function useClock() {
  const [time, setTime] = useState("--:--:--");
  useEffect(() => {
    const fmt = new Intl.DateTimeFormat("en-GB", {
      timeZone: "Asia/Kolkata",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
    const tick = () => setTime(fmt.format(new Date()));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}

// ─── Floating Particles ────────────────────────────────────────
function ContactParticles() {
  const parts = useRef(
    Array.from({ length: 20 }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      s: Math.random() * 3 + 1,
      d: Math.random() * 12 + 8,
      dl: Math.random() * 5,
    }))
  );
  return (
    <div className="ct-particles" aria-hidden="true">
      {parts.current.map((p) => (
        <motion.span
          key={p.id}
          className="ct-particle"
          style={{ left: `${p.x}%`, top: `${p.y}%`, width: p.s, height: p.s }}
          animate={{ y: [0, -20, 0], opacity: [0.08, 0.4, 0.08] }}
          transition={{ duration: p.d, repeat: Infinity, delay: p.dl, ease: "easeInOut" }}
        />
      ))}
    </div>
  );
}

// ─── Social Link ───────────────────────────────────────────────
function SocialLink({ name, url, icon, color }) {
  return (
    <motion.a
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      className="ct-soc"
      whileHover={{ scale: 1.06, y: -4 }}
      whileTap={{ scale: 0.95 }}
      style={{ "--social-color": color }}
      aria-label={name}
      title={name}
    >
      {icon}
    </motion.a>
  );
}

// ─── Contact Info row ──────────────────────────────────────────
function InfoCard({ icon, label, value, href, delay, index }) {
  const Tag = href ? "a" : "div";

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.4 }}
      transition={{ delay, duration: 0.5 }}
    >
      <Tag
        href={href}
        target={href?.startsWith("http") ? "_blank" : undefined}
        rel="noopener noreferrer"
        className={`ct-info${href ? " is-link" : ""}`}
      >
        <span className="ct-info-idx">{String(index + 1).padStart(2, "0")}</span>
        <span className="ct-info-ic">{icon}</span>
        <span className="ct-info-txt">
          <span className="ct-info-label">{label}</span>
          <span className="ct-info-val">{value}</span>
        </span>
        {href && (
          <i className="ct-info-go">
            <FiArrowUpRight />
          </i>
        )}
      </Tag>
    </motion.div>
  );
}

/* ============================================================
   CONTACT
   ------------------------------------------------------------
   Firebase + EmailJS logic is identical to your original:
   same state, same handlers, same collections/templates.
   Only markup and presentation changed.
============================================================ */

export default function Contact() {
  const rootRef = useRef(null);
  const formRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const { scrollYProgress } = useScroll();
  const progress = useSpring(scrollYProgress, { stiffness: 120, damping: 28 });
  const clock = useClock();

  useParallax(rootRef);

  const socialLinks = [
    {
      name: "Instagram",
      url: "https://www.instagram.com/karanks1436/",
      icon: <FaInstagram size={19} />,
      color: "#E1306C",
    },
    {
      name: "YouTube",
      url: "https://www.youtube.com/@Karanks1436",
      icon: <FaYoutube size={19} />,
      color: "#FF0000",
    },
    {
      name: "Facebook",
      url: "https://www.facebook.com/profile.php?id=100070773074829",
      icon: <FaFacebookF size={19} />,
      color: "#1877F2",
    },
    {
      name: "WhatsApp",
      url: "https://wa.me/9041429065",
      icon: <FaWhatsapp size={19} />,
      color: "#25D366",
    },
    {
      name: "GitHub",
      url: "https://github.com/Karanks1436",
      icon: <FaGithub size={19} />,
      color: "#e9efff",
    },
  ];

  // Auto-fill from auth
  useEffect(() => {
    if (!auth || !auth.onAuthStateChanged) return;
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setFormData((prev) => ({
          ...prev,
          name: user.displayName || prev.name,
          email: user.email || prev.email,
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const { name, email, message } = formData;

    if (!name.trim() || !email.trim() || !message.trim()) {
      toast.error("Please fill in all fields!");
      return;
    }

    setLoading(true);

    if (!db) {
      // Preview mode: simulate send.
      setTimeout(() => {
        toast.success("Message sent successfully! 🎉");
        setFormData({ name: "", email: "", message: "" });
        setSent(true);
        setTimeout(() => setSent(false), 3200);
        setLoading(false);
      }, 900);
      return;
    }

    try {
      // Save to Firebase
      await addDoc(collection(db, "Contact"), {
        name,
        email,
        message,
        date: new Date().toISOString(),
      });

      // Send email via EmailJS
      try {
        await emailjs.send(
          "service_31euj04",
          "template_nw3hgwg",
          { from_name: name, from_email: email, message },
          "gQJJ82qqno2_M07FO"
        );

        toast.success("Message sent successfully! 🎉");
        setFormData({ name: "", email: "", message: "" });
        setSent(true);
        setTimeout(() => setSent(false), 3200);
      } catch (emailError) {
        console.error("EmailJS error:", emailError);
        toast.error("Failed to send email notification, but message was saved.");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="kk-root ct-page" ref={rootRef}>
      <motion.div className="kk-progress" style={{ scaleX: progress }} />
      <ContactParticles />

      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar
        theme="dark"
        toastStyle={{
          background: "#0b0b10",
          border: "1px solid rgba(200,255,61,.35)",
          color: "#f3f3f5",
          fontFamily: "'Manrope', system-ui, sans-serif",
        }}
      />

      {/* ═══════════ HERO ═══════════ */}
      <header className="ct-hero">
        <div className="ct-hero__bg" aria-hidden="true">
          <span className="ct-hero__grid" data-plx="0.05" />
          <span className="ct-hero__glow ct-hero__glow--acid" data-plx="0.16" />
          <span className="ct-hero__glow ct-hero__glow--violet" data-plx="0.24" />
          <span className="ct-hero__word" data-plx="0.1">
            HELLO
          </span>
        </div>

        <div className="kk-wrap ct-hero__in">
          <motion.div
            className="ct-hero__copy"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ct-kicker">
              <span className="kk-dot" />
              <span>Available for freelance</span>
              <span className="kk-slash">/</span>
              <span>Bathinda, IN</span>
              <span className="kk-slash">/</span>
              <span className="kk-clock">
                <FiClock /> {clock} IST
              </span>
            </div>

            <h1 className="ct-title">
              Let&apos;s make
              <br />
              something <span className="kk-serif kk-acid">good.</span>
            </h1>

            <p className="ct-sub">
              Have a project in mind, a half-formed idea, or just want to say hello?
              Tell me what you&apos;re building — I read every message myself.
            </p>
          </motion.div>

          {/* direct lines */}
          <motion.div
            className="ct-hero__card"
            initial={{ opacity: 0, y: 26 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.12, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ct-hero__card-head">
              <span className="ct-status">
                <i /> Open to work
              </span>
              <span className="ct-hero__card-no">Direct lines</span>
            </div>

            <a className="ct-quick" href="mailto:karanksxxx@gmail.com">
              <span className="ct-quick__ic">
                <FiMail />
              </span>
              <span className="ct-quick__txt">
                <em>Email</em>
                <b>karanksxxx@gmail.com</b>
              </span>
              <FiArrowUpRight className="ct-quick__go" />
            </a>

            <a
              className="ct-quick"
              href="https://wa.me/9041429065"
              target="_blank"
              rel="noreferrer"
            >
              <span className="ct-quick__ic">
                <FaWhatsapp />
              </span>
              <span className="ct-quick__txt">
                <em>WhatsApp</em>
                <b>+91 90414 29065</b>
              </span>
              <FiArrowUpRight className="ct-quick__go" />
            </a>

            <a className="ct-quick" href="tel:9041429065">
              <span className="ct-quick__ic">
                <FiPhone />
              </span>
              <span className="ct-quick__txt">
                <em>Call</em>
                <b>Mon – Sat · 10:00 – 19:00 IST</b>
              </span>
              <FiArrowUpRight className="ct-quick__go" />
            </a>

            <span className="ct-hero__card-foot">Usually replies within 24 hours</span>
          </motion.div>
        </div>
      </header>

      {/* ═══════════ MAIN ═══════════ */}
      <main className="ct-main">
        <div className="kk-wrap ct-grid">
          {/* ─── Left: details, socials, map ─── */}
          <motion.section
            className="ct-left"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ct-sec-head">
              <span className="kk-eyebrow">01 — Details</span>
              <h2 className="kk-h2">
                Find me <span className="kk-serif kk-acid">here</span>
              </h2>
              <p className="ct-lead">
                I&apos;m always open to discussing new projects, creative ideas, or
                opportunities to be part of your vision.
              </p>
            </div>

            <div className="ct-infos">
              <InfoCard
                icon={<FiMail />}
                label="Email"
                value="karanksxxx@gmail.com"
                href="mailto:karanksxxx@gmail.com"
                delay={0.05}
                index={0}
              />
              <InfoCard
                icon={<FiPhone />}
                label="Phone"
                value="+91 90414-29065"
                href="tel:9041429065"
                delay={0.1}
                index={1}
              />
              <InfoCard
                icon={<FiMapPin />}
                label="Location"
                value="Bathinda, Punjab, India"
                delay={0.15}
                index={2}
              />
              <InfoCard
                icon={<FiMessageSquare />}
                label="WhatsApp"
                value="Chat on WhatsApp"
                href="https://wa.me/9041429065"
                delay={0.2}
                index={3}
              />
            </div>

            <div className="ct-follow">
              <div className="ct-follow__head">
                <span className="kk-eyebrow kk-eyebrow--violet">02 — Elsewhere</span>
                <span className="ct-follow__hint">Follow along</span>
              </div>
              <div className="ct-socs">
                {socialLinks.map((link) => (
                  <SocialLink key={link.name} {...link} />
                ))}
              </div>
            </div>

            <div className="ct-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1040.9124924412583!2d74.94151299934369!3d30.21995684086096!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39173295b5240803%3A0xb130d6a81d06d530!2s6W9R%2BXGQ%2C%20Jagdish%20Monga%20Street%2C%20Ganesh%20Nagar%2C%20Bathinda%2C%20Punjab%20151001!5e0!3m2!1sen!2sin!4v1739072491172!5m2!1sen!2sin"
                allowFullScreen=""
                loading="lazy"
                title="Location Map"
              />
              <span className="ct-map__frame" aria-hidden="true" />
              <span className="ct-map__badge">
                <FiMapPin /> Bathinda, PB
              </span>
              <a
                className="ct-map__open"
                href="https://maps.google.com/?q=Bathinda,Punjab,India"
                target="_blank"
                rel="noreferrer"
              >
                Open in Maps <FiArrowUpRight />
              </a>
            </div>
          </motion.section>

          {/* ─── Right: form ─── */}
          <motion.aside
            className="ct-right"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="ct-form-card" ref={formRef}>
              <div className="ct-form-head">
                <div>
                  <span className="kk-eyebrow">03 — Message</span>
                  <h2 className="ct-form-h">
                    Send a <span className="kk-serif kk-acid">message</span>
                  </h2>
                </div>
                <span className="ct-form-badge">
                  <FiSend /> Form
                </span>
              </div>

              <p className="ct-form-sub">
                Fill out the form below and I&apos;ll get back to you as soon as possible.
              </p>

              <form onSubmit={handleSubmit} className="ct-form">
                <label className="ct-field">
                  <span>
                    <FiUser /> Full name
                  </span>
                  <input
                    id="contact-name"
                    type="text"
                    name="name"
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label className="ct-field">
                  <span>
                    <FiAtSign /> Email address
                  </span>
                  <input
                    id="contact-email"
                    type="email"
                    name="email"
                    placeholder="john@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                  />
                </label>

                <label className="ct-field">
                  <span>
                    <FiMessageSquare /> Message
                  </span>
                  <textarea
                    id="contact-message"
                    name="message"
                    placeholder="Tell me about your project, idea, or just say hello..."
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    required
                  />
                  <em className="ct-count">{formData.message.length} characters</em>
                </label>

                <motion.button
                  type="submit"
                  className="kk-btn kk-btn--lg ct-submit"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {loading ? (
                    <>
                      <FiLoader className="ct-spinner" /> Sending…
                    </>
                  ) : sent ? (
                    <>Sent — thank you</>
                  ) : (
                    <>
                      <FiSend /> Send message
                    </>
                  )}
                </motion.button>

                <div className="ct-form-foot">
                  <span className="ct-form-note">
                    Saved to Firestore &amp; emailed.
                  </span>
                  <a className="kk-link" href="mailto:karanksxxx@gmail.com">
                    or email directly <FiArrowRight />
                  </a>
                </div>
              </form>
            </div>

            {/* Chatbot */}
            <div className="ct-chat">
              <Chatbot />
            </div>
          </motion.aside>
        </div>
      </main>

      <Navbar />
      <Foot />
    </div>
  );
}